import { type ForwardedRef, forwardRef, useMemo } from 'react';
import type { OptionTypeBase } from 'react-select';
import type { Props as ReactSelectCreatableProps } from 'react-select/creatable';

import { BaseSelect } from './BaseSelect';
import { SelectType } from './SelectType';
import { CreateLabel } from './subcomponents/CreateLabel';
import type { CustomizeMenuPortaling } from './types';

export type CreatableSelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
> = CustomizeMenuPortaling<ReactSelectCreatableProps<OptionType, IsMulti>> & {
  createLabelEntity?: string;
};

export const CreatableSelect = forwardRef(
  <OptionType extends OptionTypeBase, IsMulti extends boolean>(
    {
      createLabelEntity,
      createOptionPosition = 'last',
      ...props
    }: CreatableSelectProps<OptionType, IsMulti>,
    forwardedRef: ForwardedRef<unknown>,
  ) => {
    const selectProps = useMemo(
      () => ({
        formatCreateLabel: (value: string) => (
          <CreateLabel
            value={value}
            entity={createLabelEntity}
            createOptionPosition={createOptionPosition}
          />
        ),
        createOptionPosition,
        ...props,
      }),
      [createLabelEntity, createOptionPosition, props],
    );

    return (
      <BaseSelect
        customRef={forwardedRef}
        selectType={SelectType.CREATABLE}
        selectProps={selectProps}
      />
    );
  },
);
CreatableSelect.displayName = 'CreatableSelect';
